import { createSelector } from "reselect"
import { get, chain, filter, every, size, some, isEmpty, startsWith, find, isArray } from "lodash"
import { PORTFOLIO_ROOT_NAVIGATION, resolvePortfolioIdQueryParam } from "../navigation"
import { getIsSuperuser } from "."
import { getSelectedModelId, getCurrentPortfolio } from "./portfolios"
import { getIsDashboardEnabled } from "selectors/features"

const EMPTY_ARRAY = []

const getAccount = state => get(state, "global.selected.account")
const getAccountMeta = state => get(state, "global.selected.accountMeta")
const getPortfolioUrlSegment = state => get(state, "global.selected.portfolio")
const getPortfolioIntradayEnabled = state => get(getCurrentPortfolio(state), "intradayEnabled", false)
const getModelId = state => getSelectedModelId(state)
const getPathname = () => window.location.pathname
// Its own slot, keyed on the route segment, and stale in the same window as the portfolio's.
const getPortfolioExperiments = state => (
  get(state, "global.data.portfolioExperiments.id") === `portfolioExperiments-${get(state, "global.selected.portfolio")}`
    ? get(state, "global.data.portfolioExperiments.data.portfolio.experiments")
    : undefined
)

// We need to remove the Rebalance tab completely for OpPro accounts without experiments
function shouldKeepLink(plan, modelId, nbExperiments, portfolioIntradayEnabled) {
  return link => {
    // No omit conditions
    if (!link.omit) { return true }

    const { plan: linkOmitPlan, modelId: linkModelId, noExperiments, needIntraday } = link.omit
    // Restrictive rules are simpler
    const restrict = some([
      linkModelId && linkModelId === modelId,
      needIntraday && !portfolioIntradayEnabled,
      (linkOmitPlan && !noExperiments) && linkOmitPlan === plan,
      (linkOmitPlan && noExperiments) && linkOmitPlan === plan && nbExperiments === 0,
    ])

    return !restrict
  }
}

const allConditionsFilter = (item, modelId, plan, betaFeaturesEnabled, isSuperuser, nbExperiments, portfolioIntradayEnabled) => {
  const restrictCondition = item => !item.restrict || item.restrict === modelId
  const omitCondition = shouldKeepLink(plan, modelId, nbExperiments, portfolioIntradayEnabled)
  const betaCondition = item => !item.betaFeaturesEnabled || betaFeaturesEnabled
  const superuserCondition = item => !item.superuserOnly || isSuperuser

  return every([restrictCondition, omitCondition, betaCondition, superuserCondition], (fn) => fn(item))
}

function getDisabled(subItem, accountMeta) {
  const { disabled } = subItem
  // Account-root's resource, not this shell's — absent on a cold portfolio URL.
  const { plan } = accountMeta ?? {}
  if (isEmpty(disabled)) { return false }

  const omitConditionPlan = get(disabled, "plan")
  if (omitConditionPlan === plan) {
    return "Only available with enterprise plan"
  }
  return false
}

/** Resolves `to` (string or `(ctx) => string`). Paths starting with `/` are under the account root. */
function getLinkPath(account, portfolioUrlSegment, portfolioData, link) {
  const portfolioIdParam = resolvePortfolioIdQueryParam(portfolioData, portfolioUrlSegment)
  const ctx = { account, portfolioUrlSegment, portfolioIdParam }
  const rawTo = typeof link.to === "function" ? link.to(ctx) : link.to
  return startsWith(rawTo, "/")
    ? `/${account}${rawTo}`
    : `/${account}/p/${portfolioUrlSegment}/${rawTo}`
}

function linkPathnamePrefix(to) {
  return to.split("?")[0]
}

function computeSubItems(subItems, account, accountMeta, portfolioUrlSegment, parentTo, modelId, isSuperuser, portfolioIntradayEnabled) {
  const { plan, betaFeaturesEnabled } = accountMeta ?? {}

  return chain(subItems)
    .map(subItem =>
      ({
        ...subItem,
        id: subItem.to,
        to: `/${account}/p/${portfolioUrlSegment}/${parentTo}/${subItem.to}`,
        hidden: subItem.hidden,
        disabled: getDisabled(subItem, accountMeta),
      }))
    .filter(subItem => allConditionsFilter(subItem, modelId, plan, betaFeaturesEnabled, isSuperuser, 0, portfolioIntradayEnabled))
    .value()
}

function expandPortfolioNavLink(account, accountMeta, portfolioUrlSegment, portfolioData, modelId, isSuperuser, portfolioIntradayEnabled, link) {
  const parentSegment = typeof link.to === "string" ? link.to : ""
  const preferenceKey =
    typeof link.to === "string" ? linkPathnamePrefix(link.to) : link.id
  return {
    ...link,
    id: preferenceKey,
    to: getLinkPath(account, portfolioUrlSegment, portfolioData, link),
    subItems: computeSubItems(link.subItems, account, accountMeta, portfolioUrlSegment, parentSegment, modelId, isSuperuser, portfolioIntradayEnabled),
    hidden: link.hidden,
  }
}

const getHiddenFullLinks = createSelector(
  [getAccount, getAccountMeta, getPortfolioUrlSegment, getCurrentPortfolio, getModelId, getIsSuperuser, getPortfolioIntradayEnabled],
  (account, accountMeta, portfolioUrlSegment, portfolioData, modelId, isSuperuser, portfolioIntradayEnabled) => {
    const expand = link => expandPortfolioNavLink(account, accountMeta, portfolioUrlSegment, portfolioData, modelId, isSuperuser, portfolioIntradayEnabled, link)
    return PORTFOLIO_ROOT_NAVIGATION.filter(link => link.hidden).map(expand)
  },
)

const getLinksIncludeHidden = createSelector(
  [getAccount, getAccountMeta, getPortfolioUrlSegment, getCurrentPortfolio, getModelId, getPortfolioExperiments, getIsSuperuser, getPortfolioIntradayEnabled, getIsDashboardEnabled],
  (account, accountMeta, portfolioUrlSegment, portfolioData, modelId, portfolioExperiments, isSuperuser, portfolioIntradayEnabled, isDashboardEnabled) => {
    const { plan, betaFeaturesEnabled } = accountMeta ?? {}
    const expand = link => expandPortfolioNavLink(account, accountMeta, portfolioUrlSegment, portfolioData, modelId, isSuperuser, portfolioIntradayEnabled, link)
    return chain(PORTFOLIO_ROOT_NAVIGATION)
      .map(expand)
      .filter(link => {
        if (link.featureGate === "dashboard" && !isDashboardEnabled) {
          return false
        }
        return allConditionsFilter(link, modelId, plan, betaFeaturesEnabled, isSuperuser, size(portfolioExperiments), portfolioIntradayEnabled)
      })
      .value()
  },
)

function getSubLinkItems(links, pathname) {
  const parentLink = find(links, link => pathname.startsWith(linkPathnamePrefix(link.to)))

  if (!parentLink || isEmpty(parentLink.subItems) || !isArray(parentLink.subItems)) {
    return EMPTY_ARRAY
  }

  return parentLink.subItems.map(subItem => ({
    ...subItem,
    parentId: parentLink.id,
  }))
}

const getSubLinksIncludeHidden = createSelector(
  [getLinksIncludeHidden, getPathname],
  getSubLinkItems,
)

export const getFullLinks = createSelector(
  [getLinksIncludeHidden],
  (links) => filter(links, link => !link.hidden),
)

export const getSubLinks = createSelector(
  [getFullLinks, getPathname],
  getSubLinkItems,
)

export const getControls = createSelector(
  [getLinksIncludeHidden, getSubLinksIncludeHidden, getModelId, getPathname],
  (links, subLinks, modelId, pathname) => {
    if (subLinks && subLinks.length > 0) {
      return chain(subLinks)
        .filter(link => link.hidden || pathname.startsWith(linkPathnamePrefix(link.to)))
        .first()
        .get("controls", {})
        .value()
    } else {
      return chain(links)
        .filter(link => pathname.startsWith(linkPathnamePrefix(link.to)))
        .first()
        .get("controls", {})
        .value()
    }
  },
)

export const getIsNavigationDisabled = createSelector(
  [getHiddenFullLinks, getPathname],
  (links, pathname) => {
    if (!links || links.length === 0) {
      return false
    }

    return chain(links)
      .filter(link => pathname.startsWith(linkPathnamePrefix(link.to)))
      .first()
      .get("hidden", false)
      .value()
  },
)
